import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { useConfig } from 'apps/cdp-web-demo/app/(main)/ConfigContext';
import {
  authMethodOptions,
  getAuthMethodData,
  isAuthMethodData,
} from 'apps/cdp-web-demo/app/data/authMethods';
import { getHexFromAccentColor } from 'apps/cdp-web-demo/app/getHexFromAccentColor';
import { AuthMethod, AuthMethodOption } from 'apps/cdp-web-demo/app/types';
import Image from 'next/image';
import { Checkbox, CheckboxProps } from '@cbhq/cds-web/controls/Checkbox';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box } from '@cbhq/cds-web/layout';

import styles from './AuthMethods.module.css';

type ItemState =
  | {
      type: 'idle';
    }
  | {
      type: 'preview';
      container: HTMLElement;
    }
  | {
      type: 'is-dragging';
    }
  | {
      type: 'is-dragging-over';
      closestEdge: Edge | null;
    };

const idle: ItemState = { type: 'idle' };

type DragPreviewProps = {
  authMethod?: AuthMethod;
  checked?: boolean;
  readOnly?: boolean;
};

type Orientation = 'horizontal' | 'vertical';

type DropIndicatorProps = { edge: Edge; gap: string };

const edgeToOrientationMap: Record<Edge, Orientation> = {
  top: 'horizontal',
  bottom: 'horizontal',
  left: 'vertical',
  right: 'vertical',
};

const strokeSize = 2;
const terminalSize = 8;
const offsetToAlignTerminalWithLine = (strokeSize - terminalSize) / 2;

function DropIndicator({ edge, gap }: DropIndicatorProps) {
  const lineOffset = `calc(-0.5 * (${gap} + ${strokeSize}px))`;

  const orientation = edgeToOrientationMap[edge];

  return (
    <div
      style={
        {
          '--line-thickness': `${strokeSize}px`,
          '--line-offset': `${lineOffset}`,
          '--terminal-size': `${terminalSize}px`,
          '--terminal-radius': `${terminalSize / 2}px`,
          '--negative-terminal-size': `-${terminalSize}px`,
          '--offset-terminal': `${offsetToAlignTerminalWithLine}px`,
        } as CSSProperties
      }
      className={`${styles['drop-indicator']} ${styles[`orientation-${orientation}`]} ${[styles[`edge-${edge}`]]}`}
    />
  );
}

// The drag preview for the auth method
function DragPreview({ authMethod, checked, readOnly }: DragPreviewProps) {
  const {
    state: { accentColor, themeMode },
  } = useConfig();
  const data = authMethod ? authMethodOptions[authMethod] : undefined;
  return (
    <div
      style={
        {
          backgroundColor: 'var(--defaultPageBg)',
          border: '1px solid var(--defaultPageBgLine)',
          borderRadius: '0.5rem',
          fontFamily: 'var(--defaultFont-sans)',
          padding: '0.375rem 1rem',
          width: 'min(80vw, 300px)',
        } as CSSProperties
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
            }}
          >
            {data?.icon && <data.icon width="16" role="img" aria-hidden="true" />}
          </div>
          <span style={{ fontSize: '0.875rem', fontWeight: '500', lineHeight: '1.5rem' }}>
            {data?.label}
          </span>
        </div>
        <div>
          {checked && (
            <div
              style={{
                backgroundColor: getHexFromAccentColor(accentColor, themeMode),
                color: 'var(--defaultPageBg)',
                width: '1.25rem',
                height: '1.25rem',
                opacity: readOnly ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="checkmark" width="16" height="16" role="img" aria-hidden="true" />
            </div>
          )}
          {!checked && (
            <div
              style={{
                border: `1px solid var(--defaultPageBgLineHeavy)`,
                width: '1.25rem',
                height: '1.25rem',
                opacity: readOnly ? 0.5 : 1,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function AuthMethodsFieldRow({
  registerRef,
  ...props
}: CheckboxProps<AuthMethod> & {
  registerRef?: AuthMethodOption['registerRef'];
}) {
  const { checked, disabled = false, readOnly, value } = props;

  const ref = useRef<HTMLDivElement | null>(null);

  const dragHandleRef = useRef<HTMLButtonElement>(null);

  const isDragHandleDisabled = disabled || !checked || readOnly;

  const [state, setState] = useState<ItemState>(idle);

  const setElementRef = useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el;
      if (value) {
        registerRef?.(value, el);
      }
    },
    [registerRef, value],
  );

  useEffect(() => {
    const element = ref.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) {
      return () => {};
    }

    return combine(
      draggable({
        element,
        dragHandle,
        canDrag() {
          return !disabled && !!checked && !readOnly;
        },
        getInitialData() {
          return getAuthMethodData(value);
        },
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({
              x: '16px',
              y: '8px',
            }),
            render({ container }) {
              setState({ type: 'preview', container });
            },
          });
        },
        onDragStart() {
          setState({ type: 'is-dragging' });
        },
        onDrop() {
          setState(idle);
        },
      }),
      dropTargetForElements({
        element,
        canDrop({ source, element: dropTargetEl }) {
          const cb = dropTargetEl.querySelector<HTMLInputElement>('input[type="checkbox"]');
          // disallow dropping on self
          if (source.element === element) {
            return false;
          }
          // disallow dropping on read only or unchecked checkboxes
          if (cb && (cb.readOnly || !cb.checked)) {
            return false;
          }
          // only allow other auth methods to be dropped
          return isAuthMethodData(source.data);
        },
        getData({ input }) {
          const data = getAuthMethodData(value);
          return attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          });
        },
        getIsSticky() {
          return true;
        },
        onDragEnter({ self }) {
          const closestEdge = extractClosestEdge(self.data);
          setState({ type: 'is-dragging-over', closestEdge });
        },
        onDrag({ self }) {
          const closestEdge = extractClosestEdge(self.data);

          // Only need to update react state if nothing has changed.
          // Prevents re-rendering.
          setState((current) => {
            if (current.type === 'is-dragging-over' && current.closestEdge === closestEdge) {
              return current;
            }
            return { type: 'is-dragging-over', closestEdge };
          });
        },
        onDragLeave() {
          setState(idle);
        },
        onDrop() {
          setState(idle);
        },
      }),
    );
  }, [checked, disabled, readOnly, value]);

  return (
    <Box position="relative">
      <Box key={value} ref={setElementRef} display="flex" alignItems="center" gap={1} width="100%">
        <button
          className={styles['drag-handle']}
          ref={dragHandleRef}
          key={`${value}-drag-handle`}
          type="button"
          disabled={isDragHandleDisabled}
        >
          <Image src="/grip.svg" alt="Drag handle" width={16} height={16} />
        </button>
        <div className={`${styles['checkbox-cell']} ${props.disabled ? styles.disabled : ''}`}>
          <Checkbox {...props} />
        </div>
        {state.type === 'is-dragging-over' && state.closestEdge ? (
          <DropIndicator edge={state.closestEdge} gap="0.75rem" />
        ) : null}
      </Box>
      {state.type === 'preview'
        ? createPortal(
            <DragPreview readOnly={readOnly} checked={checked} authMethod={value} />,
            state.container,
          )
        : null}
    </Box>
  );
}

export { AuthMethodsFieldRow };
